import { useEffect, useRef, useState } from "react";
import { AlertCircle, Clock, Search, User } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getBulkDropdownData } from "../../api/keyvalue";
import {
  addShiftGroup,
  getShiftGroupById,
  searchGroupData,
  updateShiftGroup,
} from "../../api/shiftGroupApi";
import Grid from "../../components/Grid";
import LoadingSpinner from "../../components/LoadingSpinner";
import Tab from "../../components/Tab";
import Button from "../../components/input/Button";
import Dropdown from "../../components/input/Dropdown";
import Input from "../../components/input/Input";
import SideDrawer from "../../components/input/SideDrawer";
import ShiftGroupMasterForm from "../../components/master/callCenter/ShiftGroupMasterForm";
import ShiftGroupStakeholder from "../../components/master/callCenter/ShiftGroupStakeholder";
import ShiftGroupUsers from "../../components/master/callCenter/ShiftGroupUsers";
import {
  ActiveCellStyle,
  StatusCellStyle,
} from "../../components/renderer/StyleRenderer";
import {
  shiftGroupStkValidation,
  shiftGroupUserValidation,
  shiftGroupValidation,
} from "../../components/validations/ShiftGroupCreation";
import { FORMS, MODULES } from "../../const";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import useNextAndPreviousAuthority from "../../hooks/useNextAndPreviousAuthority";
import { errorActions } from "../../store/error-slice";
import Modal from "../../components/Modal";

const ShiftGroupMaster = () => {
  const { firstViableTab, lastViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Master,
    section: FORMS.CALL_CENTER_SHIFT_GROUP,
  });

  const errorState = useSelector((state) => state.error.shiftGroupErrors);
  const dispatch = useDispatch();
  const [tab, setTab] = useState(1);
  const [drawerTab, setDrawerTab] = useState(firstViableTab);
  const { next, prev } = useNextAndPreviousAuthority({
    sectionKey: "blacklist",
    minTab: 1,
    maxTab: 2,
    module: MODULES.Master,
    section: FORMS.CALL_CENTER_SHIFT_GROUP,
    currentTab: drawerTab,
    authenticated: true,
  });
  const user = useSelector((state) => state.user);

  const [activeAccordion1, setActiveAccordion1] = useState(true);
  const [resetVisible, setResetVisible] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editShiftGroup, setEditShiftGroup] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [searchFetchLoading, setSearchFetchLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [searchRes, setSearchRes] = useState(false);

  const [groupType, SetGroupType] = useState(null);

  //tab ref
  const userRef = useRef();
  const companyRef = useRef();
  const groupRef = useRef();

  // dropdown
  const [activeOptions, setActiveOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [yesNoOptions, setYesNoOptions] = useState([]);

  // search ref
  const codeRef = useRef(null);
  const nameRef = useRef(null);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  const deleteWarningRef = useRef();
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [pendingSubmitEvent, setPendingSubmitEvent] = useState(null);

  //search state
  const [status, setStatus] = useState(null);
  const [active, setActive] = useState(null);
  const [stkType, setStkType] = useState(null);

  const handleDrawerOpen = () => {
    setEditMode(false);
    setDrawerTab(firstViableTab);
    setEditShiftGroup(null);
    setIsDrawerOpen(true);
    SetGroupType(null);
    dispatch(errorActions.clearShiftGroupError());
  };
  useEffect(() => {
    (async () => {
      //new
      const {
        active,
        status,
        yesno: yesnoOpt,
      } = await getBulkDropdownData([
        { key: "active" },
        { key: "status" },
        { key: "yesno" },
      ]);
      setActiveOptions(active);
      setStatusOptions(status);
      setYesNoOptions(yesnoOpt);
    })();
  }, []);

  const handleReset = (e) => {
    e.preventDefault();
    if (tab === 1) {
      setSearchClicked(false);
      setResetVisible(false);
    }
  };
  const handleSearchSubmit = async (e, bypassDeleteCheck) => {
    e.preventDefault();
    if (!bypassDeleteCheck && active.current.isDeletedActive()) {
      setPendingSubmitEvent(e);
      setShowDeleteWarning(true);
      deleteWarningRef.current?.showModal();
      return;
    }
    if (tab === 1) {
      setActiveAccordion1(false);
      setResetVisible(true);
      setSearchClicked(true);
      setSearchSubmitLoading(true);
    }
    const response = await searchGroupData({
      code: codeRef.current.value,
      name: nameRef.current.value,
      from: fromDateRef.current.value,
      to: toDateRef.current.value,
      stkType: stkType,
      status: status,
      active: active,
    });
    setSearchRes(response);
    setSearchSubmitLoading(false);
  };
  const handleSearchReset = (e) => {
    e.preventDefault();
    toDateRef.current.reset();
    fromDateRef.current.reset();
    codeRef.current.reset();
    nameRef.current.reset();
    setStkType("");
    setStatus("");
    setActive("");
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    SetGroupType(null);
    setIsSubmitLoading(false);
    dispatch(errorActions.clearShiftGroupError());
  };
  const nextButtonHandler = () => {
    switch (drawerTab) {
      case 1: {
        const shiftGroupErrors = shiftGroupValidation(
          groupRef.current.getData(),
        );

        if (Object.keys(shiftGroupErrors).length > 0) {
          dispatch(
            errorActions.addShiftGroupErrorObject({
              form: "shiftGroupErrors",
              errors: shiftGroupErrors,
            }),
          );
          setIsSubmitLoading(false);
          toast.error("There was an error adding Shift group", {
            autoClose: 5000,
            containerId: "Main",
          });
          return;
        } else {
          dispatch(errorActions.clearShiftGroupError());
        }
        break;
      }
      default:
    }
    setDrawerTab(next());
  };

  const reset = () => {
    groupRef.current.resetForm();
    // groupType === "user" ? userRef.current.resetForm() : groupType === "stakeholder" ? companyRef.current.resetForm():null;
    if (groupType === "user") {
      userRef.current.resetForm();
    } else if (groupType === "stakeholder") {
      companyRef.current.resetForm();
    }
    setEditMode(false);
    setEditShiftGroup(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // setSubmitted(true);
    // setIsSubmitLoading(true);
    dispatch(errorActions.clearShiftGroupError());

    const groupFormErrors = shiftGroupValidation(groupRef.current.getData());
    let groupErrors =
      groupType === "user"
        ? shiftGroupUserValidation(userRef.current.getData())
        : groupType === "stakeholder"
          ? shiftGroupStkValidation(companyRef.current.getData())
          : null;

    if (
      Object.keys(groupFormErrors).length > 0 ||
      Object.keys(groupErrors).length > 0
    ) {
      console.log("groupErrors", groupErrors);
      try {
        dispatch(
          errorActions.validateShiftGroup({
            shiftGroupData: groupRef.current.getData(),
          }),
        );
        if (groupType === "user") {
          dispatch(
            errorActions.addShiftGroupErrorObject({
              form: "shiftGroupUserErrors",
              errors: groupErrors,
            }),
          );
        } else if (groupType === "stakeholder") {
          dispatch(
            errorActions.addShiftGroupErrorObject({
              form: "shiftGroupCompaniesErrors",
              errors: groupErrors,
            }),
          );
        }
      } catch (e) {
        setIsSubmitLoading(false);
      }

      setIsSubmitLoading(false);
      toast.error("There was an error adding shift group", {
        autoClose: 5000,
        containerId: "Main",
      });
      return;
    } else {
      if (editMode) {
        try {
          const response = await updateShiftGroup({
            shiftGroupRequest: groupRef.current.getData(),
            shiftGroupUsersRequests:
              groupType === "user" ? userRef.current.getData() : null,
            shiftGroupCompaniesRequests:
              groupType === "stakeholder" ? companyRef.current.getData() : null,
            author: user.username,
          });

          if (response !== null) {
            toast.success("Shift Group updated successfully!", {
              autoClose: 5000,
              containerId: "Main",
            });
          }
          setIsSubmitLoading(false);
          reset();
        } catch (e) {
          if (e.response.status === 409) {
            toast.error("Shift group name already exists!", {
              autoClose: 5000,
              containerId: "Main",
            });
          }
          setIsSubmitLoading(false);
        }
      } else {
        try {
          const response = await addShiftGroup({
            shiftGroupRequest: groupRef.current.getData(),
            shiftGroupUsersRequests:
              groupType === "user" ? userRef.current.getData() : null,
            shiftGroupCompaniesRequests:
              groupType === "stakeholder" ? companyRef.current.getData() : null,
            author: user.username,
          });
          if (response !== null) {
            toast.success("Shift added successfully!", {
              autoClose: 5000,
              containerId: "Main",
            });
            // reset();
          }
          setIsSubmitLoading(false);
          reset();
        } catch (e) {
          if (e.response.status === 409) {
            toast.error("Shift group name already exists!", {
              autoClose: 5000,
              containerId: "Main",
            });
          }
          setIsSubmitLoading(false);
          console.error(e);
        }
      }
    }
    setIsSubmitLoading(false);
  };
  useEffect(() => {
    if (editShiftGroup) {
      console.log("editShiftGroup", editShiftGroup);
      if (editShiftGroup?.shiftGroupCompaniesResponses != null) {
        SetGroupType("stakeholder");
      } else if (editShiftGroup?.shiftGroupUsersResponses != null) {
        SetGroupType("user");
      } else {
        SetGroupType(null);
      }
    }
  }, [editShiftGroup]);

  const deleteWarningModal = (
    <div style={{ padding: "20px" }}>
      <h3 style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <AlertCircle color="red" size="25" />
        <p>Warning</p>
      </h3>
      <p>Active is set to Deleted. Are you sure you want to proceed?</p>
      <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
        <Button
          label="Cancel"
          onClick={() => {
            deleteWarningRef.current?.close();
            setShowDeleteWarning(false);
            setPendingSubmitEvent(null);
          }}
        />
        <Button
          label="Proceed"
          className="ui red button"
          onClick={() => {
            deleteWarningRef.current?.close();
            setShowDeleteWarning(false);
            handleSubmit(pendingSubmitEvent, true);
            setPendingSubmitEvent(null);
          }}
        />
      </div>
    </div>
  );
  return (
    <>
      <div
        className="ui attached tabular"
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div className="ui tabular menu attached">
          <div
            className={`item ${tab === 1 ? "active" : ""}`}
            onClick={() => {
              setTab(1);
            }}
          >
            <span>
              <Clock size={15} style={{ marginRight: "5px" }} />
            </span>
            Shift Group Search
          </div>
        </div>
        <Button
          template="drawerOpen"
          onClick={handleDrawerOpen}
          authenticated={true}
          form={FORMS.CALL_CENTER_SHIFT_GROUP.SHF_G.form}
          module={MODULES.Master}
          editMode={false}
        />
      </div>
      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion1 ? "active" : ""}`}
            onClick={() => setActiveAccordion1(!activeAccordion1)}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span>Search</span>
                <Search size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>

              <i
                className="refresh icon primary button"
                style={{
                  display: `${resetVisible ? "flex" : "none"}`,
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  handleReset(e);
                }}
              ></i>
            </div>
          </div>
          <div className={`content ${activeAccordion1 ? "active" : ""}`}>
            {isSearchLoading ? (
              <LoadingSpinner />
            ) : (
              <form className="ui form">
                <div className="four fields">
                  <Input
                    label="Group Code"
                    placeholder="Group Code"
                    name="shiftCode"
                    ref={codeRef}
                  />
                  <Input
                    label="Group Name"
                    placeholder="Group Name"
                    name="shiftName"
                    ref={nameRef}
                  />
                  <Dropdown
                    label="Stakeholder Type"
                    options={yesNoOptions}
                    placeholder="Stakeholder Type"
                    style={{ height: "20px" }}
                    value={stkType}
                    onChange={(e, data) => {
                      setStkType(data.value);
                    }}
                  />
                </div>
                <div className="four fields">
                  <Input
                    label="from Date"
                    type="date"
                    name="empExpiaryDate"
                    placeholder="From Date"
                    ref={fromDateRef}
                  />
                  <Input
                    label="To Date"
                    type="date"
                    name="empExpiaryDate"
                    placeholder="To Date"
                    ref={toDateRef}
                  />
                  <Dropdown
                    label="Status"
                    options={statusOptions}
                    placeholder="Status"
                    style={{ height: "20px" }}
                    value={status}
                    onChange={(e, data) => {
                      setStatus(data.value);
                    }}
                  />
                  <Dropdown
                    label="Active"
                    options={activeOptions}
                    placeholder="Active"
                    style={{ height: "20px" }}
                    value={active}
                    onChange={(e, data) => {
                      setActive(data.value);
                    }}
                  />
                </div>

                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <div className="field" style={{ width: "100%" }}>
                    <Button
                      template="search"
                      onClick={(e) => {
                        handleSearchSubmit(e);
                      }}
                    />
                    <Button
                      template="searchReset"
                      onClick={(e) => {
                        handleSearchReset(e);
                      }}
                    />
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
        {searchClicked && (
          <>
            {searchSubmitLoading ? (
              <LoadingSpinner />
            ) : (
              <Grid
                isLoading={searchFetchLoading}
                content={searchRes}
                cellSizeStrategy="fitCellContents"
                // fitContentCellls={["blkAccident"]}
                config={[
                  {
                    headerName: "Id",
                    field: "shgId",
                    sort: "desc",
                    hide: true,
                  },
                  {
                    headerName: "Code",
                    field: "shgCode",
                  },
                  {
                    headerName: "Shift Group Name",
                    field: "shgGroup",
                  },
                  {
                    headerName: "Status",
                    field: "shgStatus",
                    cellRenderer: StatusCellStyle,
                  },
                  {
                    headerName: "Active",
                    field: "shgActive",
                    cellRenderer: ActiveCellStyle,
                  },
                ]}
                onCellDoubleClicked={async (props) => {
                  setSearchFetchLoading(true);
                  const response = await getShiftGroupById(props.data.shgId);
                  setEditShiftGroup(response);
                  console.log("shif group log res ", response);
                  setEditMode(true);
                  setIsDrawerOpen(true);
                  setSearchFetchLoading(false);
                  setDrawerTab(firstViableTab);
                }}
              />
            )}
          </>
        )}
      </div>

      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          editMode ? (
            <div
              className="shift-code-header"
              style={{ padding: "10px 16px", borderBottom: "1px solid #ccc" }}
            >
              <h3>Shift Code: {editShiftGroup?.shiftResponse?.shiCode}</h3>
            </div>
          ) : null
        }
        body={
          <>
            <form className="drawer-content" onSubmit={handleSubmit}>
              <div className={"shift-card-container"}>
                <div style={{ width: "60vw", boxShadow: "none" }}>
                  <div className="ui top attached tabular menu">
                    <Tab
                      Icon={Clock}
                      onClick={() => {
                        setDrawerTab(1);
                      }}
                      active={drawerTab === 1}
                      errors={
                        Object.keys(errorState.shiftGroupErrors).length !== 0
                      }
                      label="Shift Group"
                      authenticated={true}
                      form={FORMS.CALL_CENTER_SHIFT_GROUP.SHIFT_GROUP.form}
                      showMandatory={true}
                      isMandatory={true}
                    />
                    {groupType === "stakeholder" ? (
                      <Tab
                        Icon={User}
                        onClick={() => {
                          setDrawerTab(2);
                        }}
                        active={drawerTab === 2}
                        label="Stakeholders"
                        authenticated={true}
                        form={FORMS.CALL_CENTER_SHIFT_GROUP.SHIFT_STK.form}
                      />
                    ) : groupType === "user" ? (
                      <Tab
                        Icon={User}
                        onClick={() => {
                          setDrawerTab(2);
                        }}
                        active={drawerTab === 2}
                        label="Users"
                        authenticated={true}
                        form={FORMS.CALL_CENTER_SHIFT_GROUP.SHIFT_STK.form}
                      />
                    ) : null}
                  </div>

                  <div
                    className="ui bottom attached active tab segment"
                    style={{ display: drawerTab === 1 ? "block" : "none" }}
                  >
                    <ShiftGroupMasterForm
                      errors={errorState.shiftGroupErrors}
                      editMode={editMode}
                      isLoading={isLoading}
                      currentValues={
                        editShiftGroup
                          ? editShiftGroup.shiftGroupResponse
                          : null
                      }
                      SetGroupType={SetGroupType}
                      ref={groupRef}
                    />
                  </div>

                  {groupType === "stakeholder" ? (
                    <div
                      className="ui bottom attached active tab segment"
                      style={{ display: drawerTab === 2 ? "block" : "none" }}
                    >
                      <ShiftGroupStakeholder
                        errors={errorState.shiftGroupCompaniesErrors}
                        editMode={editMode}
                        ref={companyRef}
                        isLoading={isLoading}
                        currentValues={
                          editShiftGroup
                            ? editShiftGroup.shiftGroupCompaniesResponses
                            : null
                        }
                      />
                    </div>
                  ) : groupType === "user" ? (
                    <div
                      className="ui bottom attached active tab segment"
                      style={{ display: drawerTab === 2 ? "block" : "none" }}
                    >
                      <ShiftGroupUsers
                        errors={errorState.shiftGroupUserErrors}
                        editMode={editMode}
                        ref={userRef}
                        isLoading={isLoading}
                        currentValues={
                          editShiftGroup
                            ? editShiftGroup.shiftGroupUsersResponses
                            : null
                        }
                      />
                    </div>
                  ) : null}
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {drawerTab > firstViableTab && (
                        <Button
                          template="back"
                          tab={drawerTab - 1}
                          module={MODULES.Master}
                          section={FORMS.CALL_CENTER_SHIFT_GROUP}
                          authenticated={true}
                          onClick={() => setDrawerTab(prev())}
                        />
                      )}
                      {drawerTab < lastViableTab && (
                        <Button
                          disabled={groupType === null}
                          template="next"
                          tab={drawerTab + 1}
                          module={MODULES.Master}
                          section={FORMS.CALL_CENTER_SHIFT_GROUP}
                          authenticated={true}
                          onClick={nextButtonHandler}
                        />
                      )}
                      {editMode ? (
                        <Button
                          type="submit"
                          template="create"
                          isLoading={isSubmitLoading}
                          create={!editMode}
                          form={FORMS.CALL_CENTER_SHIFT_GROUP.SHF_G.form}
                          module={MODULES.Master}
                          section={FORMS.CALL_CENTER_SHIFT_GROUP}
                          authenticated={true}
                          editMode={editMode}
                        />
                      ) : drawerTab === lastViableTab && !isLoading ? (
                        <Button
                          type="submit"
                          template="create"
                          isLoading={isSubmitLoading}
                          create={!editMode}
                          form={FORMS.CALL_CENTER_SHIFT_GROUP.SHF_G.form}
                          module={MODULES.Master}
                          section={FORMS.CALL_CENTER_SHIFT_GROUP}
                          authenticated={true}
                          editMode={editMode}
                        />
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                  {/*{submitted && errorState.hasErrors && (*/}
                  {/*    <UIMessage type="negative" width="100%" centered={false}>*/}
                  {/*        <div className="content">*/}
                  {/*            <div className="header">*/}
                  {/*                There was some errors with your submission*/}
                  {/*            </div>*/}
                  {/*            {Object.keys(errorState.shiftDetailsErrors).length > 0 && (*/}
                  {/*                <p>*/}
                  {/*                    <div className="header">Employee</div>*/}
                  {/*                    <div className="content">*/}
                  {/*                        <ul>*/}
                  {/*                            {Object.entries(errorState.shiftDetailsErrors).map(*/}
                  {/*                                ([_, value]) => (*/}
                  {/*                                    <li>{value}</li>*/}
                  {/*                                ),*/}
                  {/*                            )}*/}
                  {/*                        </ul>*/}
                  {/*                    </div>*/}
                  {/*                </p>*/}
                  {/*            )}*/}
                  {/*        </div>*/}
                  {/*    </UIMessage>*/}
                  {/*)}*/}
                </div>
              </div>
            </form>
            <Modal
              ref={deleteWarningRef}
              closeButton={false}
              minWidth={"300px"}
            >
              {showDeleteWarning && deleteWarningModal}
            </Modal>
          </>
        }
        style={{ width: "60vw", boxShadow: "none" }}
      />
    </>
  );
};
export default ShiftGroupMaster;
